import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FicheConsultantComponent } from '../fiche-consultant/fiche-consultant.component';
import { OuiButton } from 'omnium-ui/button';
import { OuiIconModule } from 'omnium-ui/icon';
import { OuiDocumentViewer } from 'omnium-ui/document-viewer';
import { ConsultantDocument } from '@lib/models/consultant/consultant-document.model';
import { OuiSlidePanelService } from 'omnium-ui/layout';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  DOCUMENT_CAN_BE_UPLOADED_BO_SECTION,
  DOCUMENT_CONSULTANT_AUTOCOMPLETE_BO_OPTIONS,
  DOCUMENT_TYPE_LABELS,
  DocumentCanBeUploadedType,
  DocumentConsultantType,
} from '@lib/models/documents/document-consultant-type';
import { OuiAutocomplete } from 'omnium-ui/form-field';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { DocumentViewerConsultantService } from '@lib/components/document-viewer-consultant/document-viewer-consultant.service';
import { NgComponentOutlet } from '@angular/common';
import { OuiLoading } from 'omnium-ui/loading';
import { DocumentsConsultantService } from '@lib/services/documents/documents-consultant.service';
import { OuiButtonToggle, OuiButtonToggleOption } from 'omnium-ui/button-toggle';
import { OuiDialogService } from 'omnium-ui/dialog';
import { ConfirmationDepotDocDialogComponent } from '../fiche-consultant/tabs/confirmation-depot-doc-dialog/confirmation-depot-doc-dialog.component';
import { take } from 'rxjs';
import { DocumentService } from '@lib/services/documents/documents.service';
import {
  DOCUMENT_SECTION_OPTIONS,
  DocumentBoSectionType,
} from '@lib/models/documents/document-consultant-section';

@Component({
  selector: 'lib-fiche-consultant-panel',
  standalone: true,
  imports: [
    FicheConsultantComponent,
    OuiButton,
    OuiIconModule,
    OuiDocumentViewer,
    OuiAutocomplete,
    OuiLoading,
    OuiButtonToggle,
    NgComponentOutlet,
  ],
  templateUrl: './fiche-consultant-panel.component.html',
  styleUrl: './fiche-consultant-panel.component.scss',
  providers: [DocumentViewerConsultantService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FicheConsultantPanelComponent implements AfterViewInit {
  private readonly slidePanelService = inject(OuiSlidePanelService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly snackbarService = inject(OuiSnackbarService);
  private readonly documentViewerService = inject(DocumentViewerConsultantService);
  private readonly documentConsultantService = inject(DocumentsConsultantService);
  private readonly documentService = inject(DocumentService);
  private readonly dialogService = inject(OuiDialogService);

  readonly enableNavigation = input<boolean>(false);
  readonly disableResiliation = input<boolean>(false);

  readonly requestResiliation = output();

  @ViewChild('ficheConsultantPortalContent')
  documentViewerPortalContent?: TemplateRef<unknown>;

  @ViewChild(FicheConsultantComponent) ficheConsultant?: FicheConsultantComponent;

  documentPortal?: TemplatePortal;

  readonly isLoading = signal<boolean>(false);
  readonly isExpendSideNav = signal<boolean>(false);
  readonly refreshDocs = signal(2);
  readonly codeConsultant = signal<string | undefined>(undefined);
  readonly formComponent = this.documentViewerService.formComponent;
  readonly documentConfig = this.documentViewerService.documentConfig;
  readonly documentType = this.documentViewerService.documentType;
  readonly isDocumentFormValid = this.documentViewerService.documentFormValidity;
  readonly statutJuridiquesConsultant = this.documentConsultantService.statutJuridiques;
  readonly sectionControl = new FormControl<DocumentBoSectionType | undefined>(
    undefined,
    [Validators.required]
  );
  readonly sectionValue = signal<DocumentBoSectionType | null>(null);
  readonly typeControl = new FormControl<DocumentCanBeUploadedType | undefined>(
    undefined,
    [Validators.required]
  );
  readonly isValid = computed<boolean>(() => {
    const documentType = this.documentType();
    const config = this.documentConfig();
    const isDocumentFormValid = this.isDocumentFormValid();

    if (
      !documentType ||
      this.sectionValue() === null ||
      (config?.formComponent && !isDocumentFormValid)
    ) {
      return false;
    } else {
      return true;
    }
  });

  sectionOption = DOCUMENT_SECTION_OPTIONS;
  typeDocumentOptions = DOCUMENT_CONSULTANT_AUTOCOMPLETE_BO_OPTIONS;

  readonly viewerDocument = signal<ConsultantDocument | undefined>(undefined);
  readonly isUploadDoc = signal<boolean>(false);
  readonly slidePanelState = toSignal(this.slidePanelService.slidePanelState$);

  readonly valueToggle = signal<string>('public');
  readonly toggleOptions: OuiButtonToggleOption<string>[] = [
    { value: 'public', label: 'Public' },
    { value: 'prive', label: 'Privé' },
  ];

  readonly docTypeAlwaysPrive: DocumentConsultantType[] = [
    'mail_consultant',
    'rbe',
    'carte_demarchage_alpsi',
    'carte_demarchage_stellium_fi',
    'mandat_cif',
    'mandat_alpsi',
    'mandat_stellium_court',
    'mandat_stellium_immo',
    'mandat_stellium_financement',
    'avenant_activ_concur',
    'avenant_fin_activ_concur',
    'formulaire_relation_secondaire',
    'formulaire_transfert_client',
    'edition_consultant',
    'attestation_collab_cci',
  ];
  readonly isDocPrivateOnly = computed<boolean>(() => {
    const documentType = this.documentType();

    if (documentType && this.docTypeAlwaysPrive.includes(documentType!)) {
      return true;
    }

    return false;
  });

  constructor() {
    effect(() => {
      const state = this.slidePanelState();

      if (state === 'closed') {
        this.codeConsultant.set(undefined);
      }
    });

    effect(() => {
      if (this.statutJuridiquesConsultant().length > 0) {
        this.parseTypeDocOptions();
      }
    });

    this.sectionControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(section => {
      if (section) {
        this.sectionValue.set(section);
      }
    });

    this.typeControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(docType => {
      if (docType) {
        if (this.docTypeAlwaysPrive.includes(docType)) {
          this.valueToggle.set('prive');
        }

        this.sectionControl.setValue(DOCUMENT_CAN_BE_UPLOADED_BO_SECTION[docType]);
      }

      this.documentViewerService.documentType.set(docType ?? undefined);
    });
  }

  ngAfterViewInit(): void {
    this.documentPortal = new TemplatePortal(
      this.documentViewerPortalContent!,
      this.viewContainerRef
    );
  }

  open(codeConsultant: string) {
    this.codeConsultant.set(codeConsultant);
    this.openFicheConsultant();
  }

  private async openFicheConsultant() {
    this.ficheConsultant?.resetHistory();

    this.slidePanelService.setSlidePanelContent(this.documentPortal!);
    this.slidePanelService.openSlidePanel();
  }

  closeSlidePanel() {
    this.slidePanelService.closeSlidePanel();
    this.slidePanelService.setSlidePanelContent(null);
  }

  openDocument(event: { document: ConsultantDocument; isUploadDoc: boolean }) {
    this.viewerDocument.set(event.document);
    this.isUploadDoc.set(event.isUploadDoc);
  }

  closeDocument() {
    this.typeControl.setValue(undefined);
    this.viewerDocument.set(undefined);
  }

  expendSideNav() {
    this.isExpendSideNav.set(!this.isExpendSideNav());
  }

  async saveDocument() {
    try {
      if (this.valueToggle() === 'prive') {
        this.isLoading.set(true);

        if (this.viewerDocument()?.file && this.codeConsultant()) {
          await this.documentService.saveDocumentPriveConsultant(
            this.viewerDocument()?.file,
            this.documentType()!,
            this.sectionValue()!,
            this.codeConsultant()!
          );

          this.saveDocumentSuccess();
        } else {
          this.snackbarService.open(
            "Une erreur est survenue lors de l'enregistrement du document privé",
            'error',
            5000,
            { horizontal: 'left', vertical: 'bottom' }
          );
        }
      } else {
        this.dialogService
          .openDialog(ConfirmationDepotDocDialogComponent, {
            docType: DOCUMENT_TYPE_LABELS[this.documentType()!],
          })
          .afterClosed()
          .pipe(take(1))
          .subscribe(async val => {
            if (val) {
              this.isLoading.set(true);
              await this.documentViewerService.createOrUpdateDemandeValidationDocument(
                {
                  demandeValidationPublicId: undefined,
                  file: this.viewerDocument()?.file,
                  codeConsultant: this.codeConsultant(),
                },
                this.sectionValue()!
              );
              this.saveDocumentSuccess();
            }
          });
      }
    } catch (error) {
      console.error(error);
      this.snackbarService.open(
        "Une erreur est survenue lors de l'enregistrement du document",
        'error',
        5000,
        { horizontal: 'left', vertical: 'bottom' }
      );
      this.closeDocument();
      this.isLoading.set(false);
    }
  }

  saveDocumentSuccess() {
    this.snackbarService.open(
      `Le document ${DOCUMENT_TYPE_LABELS[this.documentType()!]} a bien été enregistré`,
      'success',
      5000,
      {
        horizontal: 'left',
        vertical: 'bottom',
      }
    );

    this.onRefreshDocs();
    this.closeDocument();
    this.valueToggle.set('public');
    this.isLoading.set(false);
  }

  onRefreshDocs() {
    this.refreshDocs.update(r => r + 1);
  }

  private parseTypeDocOptions() {
    if (!this.statutJuridiquesConsultant().find(st => st.statut === 'agent_commercial')) {
      this.typeDocumentOptions = this.typeDocumentOptions.map(typeDocs => ({
        ...typeDocs,
        children: typeDocs.children?.filter(
          ch =>
            ch.value !== 'attestation_vigilance_agc' &&
            ch.value !== 'notification_affiliation_agc' &&
            ch.value !== 'cerfa_3558_agc' &&
            ch.value !== 'cerfa_3666_agc' &&
            ch?.value !== 'rib_agc'
        ),
      }));
    }
    if (!this.statutJuridiquesConsultant().find(st => st.statut === 'societe')) {
      this.typeDocumentOptions = this.typeDocumentOptions.map(typeDocs => ({
        ...typeDocs,
        children: typeDocs.children?.filter(
          ch =>
            ch.value !== 'attestation_vigilance_soc' &&
            ch.value !== 'notification_affiliation_soc' &&
            ch.value !== 'cerfa_3558_soc' &&
            ch.value !== 'cerfa_3666_soc' &&
            ch.value !== 'rib_soc' &&
            ch.value !== 'kbis_soc' &&
            ch.value !== 'statut_societe'
        ),
      }));
    }
    if (
      !this.statutJuridiquesConsultant().find(st => st.statut === 'profession_liberale')
    ) {
      this.typeDocumentOptions = this.typeDocumentOptions.map(typeDocs => ({
        ...typeDocs,
        children: typeDocs.children?.filter(
          ch =>
            ch.value !== 'attestation_vigilance_pl' &&
            ch.value !== 'notification_affiliation_pl' &&
            ch.value !== 'cerfa_3558_pl' &&
            ch.value !== 'cerfa_3666_pl' &&
            ch.value !== 'rib_pl' &&
            ch.value !== 'kbis_pl'
        ),
      }));
    }
  }
}
